import { Input } from "@headlessui/react";
import clsx from "clsx";
import { INPUT_CLASS } from "../tableStyles";

export type Column<T> = {
  key: keyof T & string;
  label: string;
  type?: "text" | "number";
  className?: string;
  placeholder?: string;
  /** id of a <datalist> to suggest from. */
  list?: string;
};

type Props<T> = {
  label: string;
  value: T[];
  onChange: (v: T[]) => void;
  columns: Column<T>[];
  newRow: () => T;
  emptyHint?: string;
};

/**
 * One editor for every variable-length list on a device: buttons, inputs and
 * reads are structurally identical, so they share this rather than each
 * getting a bespoke component.
 */
export default function PairListEditor<T extends object>({
  label,
  value,
  onChange,
  columns,
  newRow,
  emptyHint = "None yet.",
}: Props<T>) {
  const rows = value ?? [];

  const patch = (i: number, key: string, raw: string, type?: string) => {
    const next = rows.slice();
    const parsed = type === "number" ? (raw === "" ? undefined : Number(raw)) : raw;
    next[i] = { ...next[i], [key]: parsed };
    onChange(next);
  };

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = rows.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <div className="text-sm font-medium opacity-60">{label}</div>
        <button
          type="button"
          onClick={() => onChange([...rows, newRow()])}
          className="text-xs font-bold uppercase px-2 py-1 rounded border border-slate-500/40 hover:bg-slate-500/10"
        >
          + Add
        </button>
      </div>

      {rows.length === 0 && (
        <div className="text-xs opacity-40 py-1">{emptyHint}</div>
      )}

      {rows.length > 0 && (
        <div className="flex gap-2 px-1 text-[10px] uppercase opacity-40">
          {columns.map((c) => (
            <div key={c.key} className={c.className ?? "flex-1"}>
              {c.label}
            </div>
          ))}
          <div className="w-16" />
        </div>
      )}

      {rows.map((row, i) => (
        <div key={i} className="flex gap-2 items-center">
          {columns.map((c) => (
            <div key={c.key} className={c.className ?? "flex-1"}>
              <Input
                type={c.type === "number" ? "number" : "text"}
                placeholder={c.placeholder}
                list={c.list}
                value={(row as any)[c.key] ?? ""}
                onChange={(e) => patch(i, c.key, e.target.value, c.type)}
                className={clsx(INPUT_CLASS, "py-1 px-2")}
              />
            </div>
          ))}
          <div className="w-16 flex gap-1 justify-end text-xs">
            <button
              type="button"
              disabled={i === 0}
              onClick={() => move(i, -1)}
              className="px-1 opacity-60 hover:opacity-100 disabled:opacity-20"
              aria-label="Move up"
            >
              <i className="fas fa-chevron-up" />
            </button>
            <button
              type="button"
              disabled={i === rows.length - 1}
              onClick={() => move(i, 1)}
              className="px-1 opacity-60 hover:opacity-100 disabled:opacity-20"
              aria-label="Move down"
            >
              <i className="fas fa-chevron-down" />
            </button>
            <button
              type="button"
              onClick={() => onChange(rows.filter((_, j) => j !== i))}
              className="px-1 text-red-500 hover:opacity-75"
              aria-label="Remove"
            >
              <i className="fas fa-trash" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
